/**
 * useLavanderiaLogic
 *
 * Encapsula todo el estado y lógica de la vista de Lavandería.
 * El `modo` apunta al `id` de una lavandería del catálogo (incluida la
 * genérica externa, que es una fila más con `es_generica=true`):
 *  - number (id) → tabla `lavados` filtrada por `lavanderia_id = id`.
 *  - "general"   → estado transitorio inicial antes del primer
 *                  `refreshLavanderias`. En cuanto carga el catálogo,
 *                  se migra al id de la genérica. No debería persistir
 *                  en operación normal — si llega a un save/move se
 *                  defaultea al id de la genérica como fallback.
 *
 * Round 2026-05-10 (iter 17) unificó las tablas legacy `lavanderia` y
 * `lavanderia_af` en una sola `lavados`. Iter 18 fase B (2026-05-11)
 * dropeó las legacy. Iter 24 (2026-05-11): la genérica externa pasó
 * a vivir en el catálogo como fila normal (es_generica=true), por lo
 * que la semántica `lavanderia_id IS NULL` solo aplica a huérfanos
 * — los lavados manuales sin destino del catálogo.
 *
 * El campo `source` del shape (`"af" | "general"`) sigue siendo
 * derivado por el backend para etiquetado visual, pero no decide CRUD.
 * La distinción genérica vs AF interna se da por `lavanderia.es_generica`.
 *
 * La lista de lavanderías se carga dinámicamente desde el backend,
 * así que se adapta a nuevas lavanderías sin cambios de código.
 */

import { useState, useCallback, useEffect, useMemo } from "react";
import { Form } from "antd";
import { getMsg } from "../../utils/antdStatic";
import dayjs, { Dayjs } from "dayjs";
import { useSidebar } from "../../hooks/useSidebar";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";
import {
  CADUCIDAD_FECHAS_DIAS,
  deserializarDia,
  deserializarRango,
  serializarDia,
  serializarRango,
} from "../../hooks/filtrosPersistidos";
import { finanzasEmpresaApi } from "../../services/finanzasEmpresa.api";
import { propiedadesApi } from "../../services/propiedades.api";
import { apiFetch } from "../../services/apiClient";
import { ahoraChile } from "../../utils/fechaChile";

// Round 2026-05-12 iter 28: filtro de rol para particionar lavados de la
// genérica externa en sub-tabs (Estimado vs Costo Real). Null = sin filtro
// (comportamiento previo, todas las filas — usado para AF interna).
// Round 2026-07-21: se separan "recibe" (estimado) e "intermediario"
// (pass-through neto 0) en la genérica. "recibe_intermediario" queda como
// alias legacy (los dos juntos). Ver flujo-financiero.md §7.3.
export type RolFilter =
  | null
  | "paga"
  | "recibe"
  | "intermediario"
  | "recibe_intermediario";

export interface LavadoRecord {
  id: number;
  // Round 2026-05-11 (iter 18): `source` ahora es solo etiqueta visual
  // derivada por el backend según presencia/ausencia de `lavanderia_id`
  // (`"af"` si está poblado, `"general"` si es NULL). Históricamente
  // identificaba la tabla origen (lavanderia / lavanderia_af) — ya no
  // aplica porque ambas se unificaron en `lavados`. El CRUD ya no
  // dispatcha por este campo. La distinción "genérica externa" vs "AF
  // interna" se da por `lavanderia.es_generica` del catálogo, no por
  // `source`.
  source?: "af" | "general";
  fecha?: string | null;
  propiedadTipo?: string | null;
  propiedadId?: number | null;
  cantidadLavados?: number | null;
  cantidadSecados?: number | null;
  precioLavado?: number | null;
  precioSecado?: number | null;
  monto: number;
  descripcion?: string | null;
  pagadoPor?: { tipo: number; nombre: string; usuario_id?: number | null } | null;
  pagadoA?: { tipo: number; nombre: string; usuario_id?: number | null } | null;
  rolDeAlojaFacil?: string | null;
  extraDetalle?: string | null;
  extraMonto?: number | null;
  // Vínculo con la reserva que originó este lavado. Si está seteado, el
  // registro vino de un webhook de Stays / cascada de booking, no fue
  // creado a mano. La UI muestra chip "Arriendo #X" y bloquea
  // editar/eliminar — para corregir hay que crear otro registro (lavado
  // paralelo o gasto operacional).
  bookingId?: number | null;
  bookingIdReserval?: string | null;
  /**
   * `bookingIdReservac` (formato JS11J) = código corto de la plataforma
   * (Stays/Airbnb/etc.); la UI lo muestra como «Ref. plataforma» / chip
   * «Arriendo JS11J» solo cuando existe. Si no (reserva manual), cae al
   * «Código de estadía AF» («Arriendo #N»). `bookingIdReserval` (Mongo
   * ObjectId de Stays) queda como info de trazabilidad en el tooltip.
   * (El `numero_corto` AF-… quedó legacy — round 2026-07-20.)
   */
  bookingIdReservac?: string | null;
  /**
   * Round 2026-05-08 (iter 12) — campos del booking para renderizar un
   * tooltip rico al hacer hover sobre el chip "Arriendo XXX" en la
   * columna Origen. Mismo formato que el tooltip del calendario para
   * mantener consistencia visual. Todos opcionales: si el row es
   * "Manual" (sin booking_id), todos vienen null.
   */
  bookingNombreHuesped?: string | null;
  bookingReservaTotalClp?: number | null;
  bookingPrecioNochesClp?: number | null;
  bookingIngreso?: string | null;
  bookingSalida?: string | null;
  bookingFechaCreacion?: string | null;
  // Round 2026-05-12 iter 28: nombre opcional para el modal simplificado
  // de "Costo real" de la genérica externa. Pensado como concepto/etiqueta
  // (ej. "Lavandería Tomás", "Pago a transportista X").
  nombre?: string | null;
}

export interface PropiedadOpcion {
  id: number;
  nombre: string;
}

export interface LavanderiaOpcion {
  id: number;
  nombre: string;
  activo: boolean;
  // `es_generica = true` identifica la "Lavandería General Externa" del
  // catálogo (fila no eliminable del sistema). El dropdown la muestra
  // con tag "Sistema" pero por lo demás se trata como cualquier otra
  // entrada del catálogo: el filtro pasa su `id` y el backend hace
  // `WHERE lavanderia_id = id`.
  es_generica?: boolean;
}

/**
 * Modo del dropdown:
 *   - "general" → fallback cuando todavía no se cargó el catálogo. El
 *     backend lo traduce a `WHERE lavanderia_id IS NULL` (lavados
 *     huérfanos sin lavandería del catálogo asignada). El dropdown no
 *     ofrece esta opción explícitamente — se migra al id de la genérica
 *     en `refreshLavanderias`.
 *   - number → id de cualquier lavandería del catálogo. Backend filtra
 *     `WHERE lavanderia_id = id`.
 */
export type ModoLavanderia = "general" | number;

// Round 2026-05-08 (iter 6): exportado para que el botón "Exportar a XLS"
// pueda normalizar las filas crudas del endpoint con la misma lógica que
// usa la tabla. Sin esto, el export quedaba con campos vacíos cuando el
// backend devolvía snake_case (propiedad_id, rol_de_aloja_facil, etc.)
// porque el handler los buscaba por su nombre camelCase.
export const mapLavadoRow = (r: any): LavadoRecord => ({
  id: r.id,
  // El backend siempre envía source ("af" si lavanderia_id != null,
  // "general" si no). Fallback "general" defensivo por si llega null.
  source: r.source ?? "general",
  fecha: r.fecha ?? null,
  propiedadTipo: r.propiedadTipo ?? r.propiedad_tipo ?? null,
  propiedadId: r.propiedadId ?? r.propiedad_id ?? null,
  cantidadLavados: r.cantidadLavados ?? r.cantidad_lavados ?? null,
  cantidadSecados: r.cantidadSecados ?? r.cantidad_secados ?? null,
  precioLavado: r.precioLavado ?? r.precio_lavado ?? null,
  precioSecado: r.precioSecado ?? r.precio_secado ?? null,
  monto: r.monto ?? 0,
  descripcion: r.descripcion ?? null,
  nombre: r.nombre ?? null,
  pagadoPor: r.pagadoPor ?? r.pagado_por ?? null,
  pagadoA: r.pagadoA ?? r.pagado_a ?? null,
  rolDeAlojaFacil: r.rolDeAlojaFacil ?? r.rol_de_aloja_facil ?? null,
  extraDetalle: r.extraDetalle ?? r.extra_detalle ?? null,
  extraMonto: r.extraMonto ?? r.extra_monto ?? 0,
  bookingId: r.bookingId ?? r.booking_id ?? null,
  bookingIdReserval: r.bookingIdReserval ?? r.booking?.id_reserval ?? null,
  bookingIdReservac: r.bookingIdReservac ?? r.booking?.id_reservac ?? null,
  // Round 2026-05-08 (iter 12) — campos del booking para tooltip rico.
  bookingNombreHuesped:
    r.bookingNombreHuesped ?? r.booking?.nombre_huesped ?? null,
  bookingReservaTotalClp:
    r.bookingReservaTotalClp ?? r.booking?.reserva_total_clp ?? null,
  bookingPrecioNochesClp:
    r.bookingPrecioNochesClp ?? r.booking?.precio_noches_clp ?? null,
  bookingIngreso: r.bookingIngreso ?? r.booking?.ingreso ?? null,
  bookingSalida: r.bookingSalida ?? r.booking?.salida ?? null,
  bookingFechaCreacion:
    r.bookingFechaCreacion ?? r.booking?.fecha_creacion ?? null,
});

export const useLavanderiaLogic = () => {
  const { sidebarOpen, toggleSidebar } = useSidebar();

  // ── Filtros recordados (round 2026-09-24) ────────────────────────────────
  // El drill-down desde /finanzas-empresa (?lavanderiaId&mes / &desde&hasta)
  // manda sobre lo guardado: si el admin llega por ese enlace tiene que ver
  // ESA lavandería y ESE período, y SIN filtros residuales encima (propiedad,
  // montos, extras) — la tabla y las cards de arriba salen de la MISMA
  // consulta, así que un filtro viejo hace que el detalle no cuadre con la
  // card que el admin acaba de clickear. Por eso `restaurar` va en todos los
  // filtros que recortan filas. Se mira una sola vez al montar porque el
  // efecto que lo aplica (lavanderia.tsx) limpia los parámetros de la URL
  // después de usarlos.
  const [hayEnlaceDirecto] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return Boolean(
        p.get("lavanderiaId") ||
          p.get("mes") ||
          (p.get("desde") && p.get("hasta")),
      );
    } catch {
      return false;
    }
  });
  const restaurar = !hayEnlaceDirecto;

  // `modo` se recuerda: es el filtro que decide toda la vista. Un id de una
  // lavandería borrada no rompe nada — `refreshLavanderias` cae a la genérica.
  const [modo, setModo, setModoSinRecordar] = useFiltroPersistido<ModoLavanderia>(
    "lavanderia.lavanderia",
    "general",
    {
      validar: (v) => v === "general" || (typeof v === "number" && v > 0),
      restaurar,
    },
  );
  const [lavanderias, setLavanderias] = useState<LavanderiaOpcion[]>([]);

  const [data, setData] = useState<LavadoRecord[]>([]);
  const [total, setTotal] = useState(0);
  // La PÁGINA no se recuerda a propósito: volver y aterrizar en la página 7
  // (que con otros filtros ya no existe) desorienta. El tamaño sí: es
  // preferencia de lectura y no puede dejar la tabla vacía.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useFiltroPersistido(
    "lavanderia.filas",
    20,
    { validar: (v) => typeof v === "number" && v > 0 && v <= 200 },
  );
  const [loading, setLoading] = useState(false);
  // Round 2026-05-12 iter 28: filtro de rol AF para particionar la vista
  // de la genérica externa en sub-tabs (Presupuesto estimado / Costo real).
  // Null = sin filtro (AF interna mantiene todo en una tabla).
  // Se recuerda junto con `modo`, que es lo que lo hace visible: al cambiar de
  // lavandería `handleModoChange` lo vuelve a null y ese null también se
  // guarda, así que no puede quedar un rol activo sin sub-tabs que lo muestren.
  // El OTRO camino que lo mueve —el efecto de lavanderia.tsx que lo sincroniza
  // con la lavandería elegida— usa `setRolFilterSinRecordar`: ese null/'recibe'
  // lo deduce el sistema al cargar el catálogo, no lo eligió el admin, y
  // guardarlo le borraba el sub-tab que sí había elegido.
  const [rolFilter, setRolFilter, setRolFilterSinRecordar] =
    useFiltroPersistido<RolFilter>("lavanderia.rol", null, {
      validar: (v) =>
        v === null ||
        v === "paga" ||
        v === "recibe" ||
        v === "intermediario" ||
        v === "recibe_intermediario",
      restaurar,
    });
  // Round 2026-05-12 iter 34 (antes iter 28 "simpleMode"): modo del modal
  // de creación. Cuando true, el LavadoForm:
  //  - Muestra el campo `nombre` (concepto del costo) en sección General.
  //  - Oculta sección "Detalle del lavado" + sección "Extras" (no aplican
  //    a un costo agregado anotado a mano).
  //  - Bloquea el destino "Lavandería" en la Genérica Externa cuando el
  //    rol AF es 'paga' (caso típico costo real).
  //  - MANTIENE visible la sección de Pagos completa — el costo real es
  //    un movimiento financiero con su estructura completa (rol AF +
  //    pagado_por + pagado_a). Antes (iter 28) la ocultábamos por
  //    "simpleMode", lo que dejaba al admin sin poder corregir el
  //    pagador o cambiar a intermediario en casos atípicos.
  const [costoRealMode, setCostoRealMode] = useState(false);
  // Agregados que devuelve el endpoint para los Statistic cards
  // (Estimado vs Real). Total en el período/filtro actual, no solo la
  // página. Ver finanzasEmpresa.controller.js → lavadosEmpresa.
  const [totalIngresoEstimado, setTotalIngresoEstimado] = useState(0);
  const [totalCostoReal, setTotalCostoReal] = useState(0);
  // Round 2026-07-21: total del pass-through 'intermediario' de la genérica
  // (card neutra "Intermediado (neto 0)"). No afecta la utilidad.
  const [totalIntermediado, setTotalIntermediado] = useState(0);

  // Round 2026-05-08: dos modos temporales — el usuario elige cuál usar.
  //  - "mes" (default): un mes calendario completo. Cómodo para el caso
  //    "ver todo abril" sin manipular dos pickers.
  //  - "rango": RangePicker tradicional para casos atípicos.
  // Antes solo había RangePicker (modo "rango") y `dateRange = null` →
  // sin filtro. Ahora `dateRange` se deriva de `dateMode` + (mes o rango).
  // Las tres caducan a los 7 días: el default es «el mes actual» y las cards
  // de arriba (Presupuesto estimado / Costo real / Utilidad) son del período
  // filtrado — restaurar un mes cerrado de hace semanas se lee como si fuera
  // el mes en curso.
  const [dateMonth, setDateMonth, setDateMonthSinRecordar] = useFiltroPersistido<
    Dayjs | null
  >(
    "lavanderia.mes",
    () => ahoraChile(),
    {
      caducaEnDias: CADUCIDAD_FECHAS_DIAS,
      serializar: serializarDia,
      deserializar: deserializarDia,
      restaurar,
    },
  );
  const [dateRange, setDateRange, setDateRangeSinRecordar] = useFiltroPersistido<
    [Dayjs, Dayjs] | null
  >(
    "lavanderia.rango",
    null,
    {
      caducaEnDias: CADUCIDAD_FECHAS_DIAS,
      serializar: serializarRango,
      deserializar: (crudo) => {
        const r = deserializarRango(crudo);
        if (r === undefined) return undefined;
        if (r === null) return null;
        // Acá el rango es "las dos fechas o ninguna": media fecha no filtra.
        return r[0] && r[1] ? ([r[0], r[1]] as [Dayjs, Dayjs]) : undefined;
      },
      restaurar,
    },
  );
  // El modo se lee DESPUÉS del rango a propósito: son dos claves con su propio
  // `at`, así que el modo puede sobrevivir a las fechas que le daban sentido
  // (basta que el último toque haya sido al selector Mes/Rango). Restaurar
  // 'rango' con el rango ya vencido deja la consulta SIN ventana de fecha: la
  // tabla lista todo el histórico y las cards de arriba (Presupuesto estimado /
  // Costo real / Utilidad) lo suman como si fuera el período. Por eso 'rango'
  // solo vale si el rango llegó vivo; si no, se arranca en el default seguro
  // ('mes' + mes actual) y la clave incoherente se descarta.
  const [dateMode, setDateMode, setDateModeSinRecordar] = useFiltroPersistido<
    "mes" | "rango"
  >(
    "lavanderia.modoFecha",
    "mes",
    {
      caducaEnDias: CADUCIDAD_FECHAS_DIAS,
      validar: (v) => v === "mes" || (v === "rango" && dateRange !== null),
      restaurar,
    },
  );
  const [filtroPropiedad, setFiltroPropiedad] = useFiltroPersistido<number[]>(
    "lavanderia.propiedades",
    [],
    {
      validar: (v) => Array.isArray(v) && v.every((x) => typeof x === "number"),
      restaurar,
    },
  );
  const [montoMin, setMontoMin] = useFiltroPersistido<number | null>(
    "lavanderia.montoMin",
    null,
    { validar: (v) => v === null || typeof v === "number", restaurar },
  );
  const [montoMax, setMontoMax] = useFiltroPersistido<number | null>(
    "lavanderia.montoMax",
    null,
    { validar: (v) => v === null || typeof v === "number", restaurar },
  );
  const [extraSearch, setExtraSearch] = useFiltroPersistido<string>(
    "lavanderia.busquedaExtras",
    "",
    { validar: (v) => typeof v === "string", restaurar },
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<LavadoRecord | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  // Modal para elegir destino al mover lavado externo → lavandería AF
  const [moverModalOpen, setMoverModalOpen] = useState(false);
  const [moverRecordId, setMoverRecordId] = useState<number | null>(null);
  const [moverDestinoId, setMoverDestinoId] = useState<number | null>(null);
  // Round 2026-05-12 iter 37: si el admin disparó el mover desde un
  // registro con booking_id != null y ya confirmó la advertencia de
  // desincronización, propagamos el flag al PATCH cuando elija destino.
  const [moverConfirmDesync, setMoverConfirmDesync] = useState(false);

  const [propiedades, setPropiedades] = useState<PropiedadOpcion[]>([]);

  // Round 2026-05-11 iter 24: rol "otro" renombrado a "trabajador" para
  // alinearse con el resto del sistema (round 2026-04-30 decies). El
  // backend ya normalizaba ambos al mismo rol DB; el cambio acá es de
  // UX/coherencia: ahora el bucket recibe los trabajadores reales que
  // /trabajadores administra y el inline-create los manda al rol correcto.
  //
  // La clave "lavanderia_catalogo" cubre a CUALQUIER fila del catálogo
  // `lavanderias` — tanto una AF interna como la Lavandería General Externa
  // (`es_generica=true`), que NO es AF. Se llamaba "lavanderia_af" hasta el
  // round 2026-07-26: ese nombre mentía, porque daba a entender que solo
  // aplicaba a las AF internas. El rename es puramente interno del módulo —
  // la clave NUNCA viaja al backend como decisión: `buildPagoFromRol` la
  // traduce a `{ tipo: 5, usuario_id }` antes de enviar, y el endpoint de
  // lavados lee ese objeto.
  //
  // Y NO se debe confundir con el "receptor externo" de /flujos: ese
  // también viaja como tipo=5, pero en la tabla `gastos` y con
  // `recipient_id` → `expense_recipients.id`. Un receptor externo es
  // cualquier OTRA EMPRESA que recibe plata de AF y no usa el sistema
  // (internet, agua, impuestos, tiendas de insumos, contador), JAMÁS una
  // lavandería: los pagos a lavandería (AF interna o General Externa) se
  // registran acá, sobre `lavados`, nunca como gasto a un receptor
  // externo — hacerlo los vuelve invisibles para la reconciliación de
  // /lavanderia y los cuenta dos veces en la utilidad. En `lavados` el
  // tipo=5 siempre lleva `usuario_id` → `lavanderias.id` y nunca
  // `recipient_id`.
  type Rol = "administrador" | "propietario" | "arrendatario" | "aseador" | "lavanderia_catalogo" | "trabajador";

  const ROLES: { value: Rol; label: string }[] = [
    { value: "administrador", label: "Aloja Fácil" },
    // E3 (round 2026-06-20, D-A): tipo=1 = Cliente-gestor. Value "propietario"
    // se conserva (contrato backend → tipo=1); solo cambia el label.
    { value: "propietario", label: "Cliente" },
    { value: "arrendatario", label: "Arrendatario" },
    { value: "aseador", label: "Aseador" },
    // Round 2026-05-12 iter 34: el label "Lavandería AF" antes englobaba
    // a TODAS las lavanderías del catálogo (tipo=5), incluyendo la
    // Genérica Externa — pero la genérica NO es una lavandería AF. Mismo
    // tipo=5 por debajo, pero conceptualmente distinto. Renombramos a
    // "Lavandería" (a secas) y en el LavadoForm agrupamos visualmente
    // "AF internas" vs "General Externa" con OptGroup.
    // El `value` "lavanderia_catalogo" dice justo lo que significa:
    // cualquier lavandería del catálogo, no solo una AF interna (antes se
    // llamaba "lavanderia_af" y mentía). Tampoco es el receptor externo de
    // /flujos — ese es tipo=5 en `gastos`, con `recipient_id`, y es otra
    // empresa cualquiera, nunca una lavandería.
    { value: "lavanderia_catalogo", label: "Lavandería" },
    // tipo=4 = Trabajador (empleado sin login: mantención, supervisor,
    // contador). Se rotulaba "Otro" hasta el round 2026-04-30 decies; ese
    // nombre ya no se usa (además chocaba con el receptor semilla llamado
    // "Otro" de /flujos, que es tipo=5 en `gastos`).
    { value: "trabajador", label: "Trabajador" },
  ];

  const [usuarios, setUsuarios] = useState<{ id: number; rol: string; nombre: string }[]>([]);

  const fetchUsuarios = useCallback(async () => {
    const json = await apiFetch<any>("/users/finanzas");
    const rows = Array.isArray(json) ? json : Array.isArray(json?.data) ? json.data : [];
    setUsuarios(rows.map((u: any) => ({
      id: u.id,
      rol: Array.isArray(u.rol) ? u.rol[0] : u.rol,
      nombre: u.nombre ?? u.nombre_propietario ?? u.nombre_usuario ?? `Usuario ${u.id}`,
    })));
  }, []);

  const usuariosPorRol = useMemo(() => {
    const m = new Map<Rol, { id: number; nombre: string }[]>();
    ROLES.forEach((r) => m.set(r.value, []));
    usuarios.forEach((u) => {
      const r = u.rol as Rol;
      if (m.has(r)) m.get(r)!.push(u);
    });
    return m;
  }, [usuarios]);

  const buildPagoFromRol = (rol: Rol, usuarioId: number | null | undefined) => {
    if (rol === "administrador") return { tipo: 0 as const, nombre: "Aloja Fácil", usuario_id: null };
    if (rol === "arrendatario") return { tipo: 2 as const, nombre: "Arrendatario", usuario_id: null };
    if (rol === "lavanderia_catalogo") {
      // Round 2026-05-12 iter 34: el value interno mapea a tipo=5 y el
      // `nombre` se toma del catálogo real (incluye la Genérica Externa).
      // Fallback
      // genérico = "Lavandería" (sin AF) para no mentir si no se
      // resuelve la lavandería del catálogo. El `usuario_id` que sale de
      // acá es un `lavanderias.id`: en `lavados` el tipo=5 nunca lleva
      // `recipient_id` (eso pertenece al receptor externo de `gastos`).
      const lav = lavanderias.find((l) => l.id === usuarioId);
      return { tipo: 5 as const, nombre: lav?.nombre || "Lavandería", usuario_id: usuarioId ?? null };
    }
    const u = usuarios.find((x) => x.id === usuarioId);
    if (rol === "propietario") return { tipo: 1 as const, nombre: u?.nombre || "Cliente", usuario_id: usuarioId ?? null };
    if (rol === "aseador") return { tipo: 3 as const, nombre: u?.nombre || "Aseador", usuario_id: usuarioId ?? null };
    // rol "trabajador" → tipo 4 (mismo bucket que el resto del sistema)
    return { tipo: 4 as const, nombre: u?.nombre || "Trabajador", usuario_id: usuarioId ?? null };
  };

  // Inline create de trabajador desde el dropdown "Pagado por/a (usuario)".
  // Antes mandaba `rol: "otro"` (legacy, el backend lo aliasaba a "trabajador").
  // Round 2026-05-11 iter 24: usamos el rol canónico directo.
  const crearOtro = async (nombre: string) => {
    const res = await apiFetch<any>("/users/simple", {
      method: "POST",
      body: JSON.stringify({ rol: "trabajador", nombre }),
    });
    const nuevo = { id: res.id, rol: "trabajador", nombre: res.nombre || nombre };
    setUsuarios((prev) => [...prev, nuevo]);
    return nuevo;
  };

  /**
   * Refresca el listado de lavanderías del catálogo. Se llama al montar el
   * componente y también desde el tab "Gestión de lavanderías" después de
   * cada mutación (create/update/delete) para mantener sincronizados ambos
   * tabs — antes el dropdown del tab "Registros" seguía mostrando la
   * lavandería borrada hasta hacer un refresh manual.
   *
   * Si la lavandería seleccionada en `modo` ya no existe en la nueva lista
   * (porque fue eliminada desde el otro tab), reseteamos el modo a "general"
   * para que el dropdown no quede en un estado inválido apuntando a un id
   * fantasma.
   */
  const refreshLavanderias = useCallback(async () => {
    try {
      const res = await finanzasEmpresaApi.getLavanderias();
      if (!res?.data) return;
      setLavanderias(res.data);
      // El modo apunta al `id` de la lavandería (no al string "general")
      // porque la genérica también vive en el catálogo, así que la
      // filtramos como cualquier otra: `WHERE lavanderia_id = id`.
      //   - Si el modo actual era "general" (default histórico) y
      //     existe la genérica → migramos al id de la genérica.
      //   - Si el modo apunta a un id que ya no existe → caemos a
      //     la genérica (o a "general" como fallback si no hay).
      setModo((current) => {
        const generica = res.data.find((l) => l.es_generica);
        const fallback = generica?.id ?? "general";
        if (current === "general") return fallback;
        const existe = res.data.some((l) => l.id === current);
        return existe ? current : fallback;
      });
    } catch {
      getMsg()?.error("Error al cargar lavanderías");
    }
  }, [setModo]);

  useEffect(() => {
    refreshLavanderias();
  }, [refreshLavanderias]);

  useEffect(() => {
    propiedadesApi
      .getAll()
      .then((res) => {
        if (!res?.data) return;
        setPropiedades(
          res.data.map((p: any) => ({
            id: p.id,
            nombre: p.nombrepropiedad ?? `Propiedad ${p.id}`,
          })),
        );
      })
      .catch(() => { getMsg()?.error("Error al cargar propiedades"); });
    fetchUsuarios();
  }, [fetchUsuarios]);

  // Round 2026-05-08 (iter 2): deriva el rango efectivo según el modo
  // elegido. CRITICAL: tiene que ser useMemo. Sin memoizar, cada render
  // del componente padre recreaba un nuevo array → cambiaba la
  // identidad de `effectiveRange` → `buildBody` (useCallback con dep
  // `effectiveRange`) cambiaba → `fetchData` (useCallback con dep
  // `buildBody`) cambiaba → `useEffect([fetchData])` disparaba un
  // refetch → render → loop infinito. El usuario reportó que cambiar
  // entre Mes/Rango dejaba el spinner parpadeando y el backend
  // respondía 429 (Too Many Requests). Round 2026-05-08 iter 6.
  const effectiveRange = useMemo<[Dayjs, Dayjs] | null>(() => {
    if (dateMode === "rango") return dateRange;
    if (dateMode === "mes" && dateMonth) {
      return [dateMonth.startOf("month"), dateMonth.endOf("month")];
    }
    return null;
  }, [dateMode, dateMonth, dateRange]);

  const buildBody = useCallback(
    (p: number, size: number) => {
      const body: Record<string, any> = { page: p, page_size: size };

      const fields: Record<string, any> = {};
      // Round 2026-05-08: filtro de fecha como `fields.fecha` explícito,
      // NO como `body.globalDateRange`. Razón: el backend tiene una
      // heurística "rolling 10 días" en `applyGlobalDateToField` que
      // entra cuando `now.date() < 10` Y el rango pedido coincide con
      // el mes actual completo. Reemplaza el rango con los últimos 10
      // días, lo que en mayo (día 8) hace que aparezcan registros de
      // abril aunque el usuario haya filtrado solo mayo. El guard
      // `hasExplicitDateFilter` del helper evita el rolling cuando ya
      // hay filtro explícito sobre `fecha` — usamos ese mecanismo.
      if (effectiveRange) {
        fields.fecha = {
          between: [
            effectiveRange[0].format("YYYY-MM-DD"),
            effectiveRange[1].format("YYYY-MM-DD"),
          ],
        };
      }
      if (filtroPropiedad.length > 0)
        fields.propiedad_id = { in: filtroPropiedad };
      if (montoMin != null || montoMax != null) {
        fields.monto = {
          eq: { min: montoMin ?? undefined, max: montoMax ?? undefined },
        };
      }
      if (extraSearch.trim())
        fields.extra_detalle = { contains: extraSearch.trim() };
      if (Object.keys(fields).length > 0) body.filters = { fields };

      // Round 2026-05-12 iter 28: el rolFilter va a nivel body (no fields)
      // porque el backend lo aplica via literal SQL después de
      // buildWhereFromFields. Ver lavadosEmpresa controller.
      if (rolFilter) body.rolFilter = rolFilter;

      return body;
    },
    [effectiveRange, filtroPropiedad, montoMin, montoMax, extraSearch, rolFilter],
  );

  const fetchData = useCallback(
    async (p = 1, size?: number) => {
      const resolvedSize = size ?? pageSize;
      setLoading(true);
      try {
        const body = buildBody(p, resolvedSize);
        // En modo numérico inyectamos lavanderia_id dentro de
        // filters.fields para que buildWhereFromFields lo procese. En
        // modo "general" (estado transitorio antes del primer
        // refreshLavanderias) omitimos el filter — el backend devuelve
        // huérfanos (`lavanderia_id IS NULL`). Round 2026-05-12 iter 32:
        // los huérfanos NULL son data legacy fantasma; en operación
        // normal nunca se llega acá porque `refreshLavanderias` ya migró
        // el modo al id de la genérica.
        if (typeof modo === "number") {
          const existingFields = body.filters?.fields ?? {};
          body.filters = {
            ...body.filters,
            fields: { ...existingFields, lavanderia_id: { eq: modo } },
          };
        }
        const res = await finanzasEmpresaApi.searchLavados(body);
        if (!res) return;
        setData((res.data ?? []).map(mapLavadoRow));
        setTotal(res.total ?? 0);
        setPage(p);
        // Round 2026-05-12 iter 28: agregados Estimado/Real para los
        // Statistic cards de la vista Genérica Externa. El backend ya
        // devuelve ambos por defecto en cualquier consulta — los usamos
        // solo cuando esGenérica para no contaminar otras vistas.
        setTotalIngresoEstimado(Number((res as any).totalIngresoEstimado) || 0);
        setTotalCostoReal(Number((res as any).totalCostoReal) || 0);
        setTotalIntermediado(Number((res as any).totalIntermediado) || 0);
      } catch (err: any) {
        getMsg()?.error(err.message || "Error al cargar lavandería");
      } finally {
        setLoading(false);
      }
    },
    [modo, buildBody, pageSize],
  );

  // Refetch cuando cambian modo o filtros
  useEffect(() => {
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData]);

  // â”€â”€â”€ Cambiar modo (resetea paginaciÃ³n y datos) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const handleModoChange = (nuevoModo: ModoLavanderia) => {
    setModo(nuevoModo);
    setPage(1);
    setData([]);
    setTotal(0);
    // Round 2026-05-12 iter 28: limpiamos rolFilter al cambiar de
    // lavandería para evitar que un filtro de Costo Real (genérica) quede
    // activo al pasar a una AF interna.
    setRolFilter(null);
  };

  // El mismo cambio de lavandería, pero cuando lo impone el enlace directo de
  // /finanzas-empresa: esa lavandería no la eligió el admin, así que ni ella ni
  // el reseteo del sub-tab se guardan como preferencia (ver
  // docs/filtros-persistentes.md §5). Lo demás es idéntico a `handleModoChange`:
  // cambiar de lavandería deja la página y los datos en pantalla sin sentido.
  const handleModoChangeSinRecordar = (nuevoModo: ModoLavanderia) => {
    setModoSinRecordar(nuevoModo);
    setPage(1);
    setData([]);
    setTotal(0);
    setRolFilterSinRecordar(null);
  };

  // â”€â”€â”€ CRUD Modal â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const handleNuevo = () => {
    setEditingRecord(null);
    setCostoRealMode(false);
    form.resetFields();
    // Defaults (round 2026-05-12 iter 38):
    //
    // Regla universal del modal MANUAL en /lavanderia: la lavandería
    // seleccionada en el filtro SIEMPRE es la receptora del pago (sin
    // importar si es la Genérica Externa o una AF interna). El admin
    // solo puede variar quién PAGA (AF, propietario, arrendatario,
    // aseador, trabajador) y el rol AF (paga / intermediario). Esto NO
    // afecta a los lavados AUTO de la cascada del booking — que siguen
    // su lógica propia (lavadoSync.js) y no pasan por este handler.
    //
    //  - fecha = hoy.
    //  - rolDeAlojaFacil = "intermediario" (round 2026-07-21). El modal
    //    "Nuevo registro" se usa en una AF interna, y una AF interna NO admite
    //    rol='paga' (el backend lo rechaza con 400). El flujo normal de una AF
    //    interna es intermediario: AF le cobra al huésped/cliente y le
    //    transfiere el monto a la sub-entidad. Antes el default era "paga" y
    //    esta creación manual SIEMPRE fallaba. Ver flujo-financiero.md §7.3.
    //  - pagadoPorRol = SIN default: en intermediario AF no puede ser quien
    //    paga (por eso el form deshabilita "Aloja Fácil" en "Pagado por"); el
    //    admin elige a quién se le cobra.
    //  - pagadoARol = "lavanderia_catalogo" (SIEMPRE, locked en el form).
    //  - pagadoAUsuarioId = id de la lavandería filtrada (SIEMPRE locked).
    const lavSeleccionada =
      typeof modo === "number" ? lavanderias.find((l) => l.id === modo) : null;
    form.setFieldsValue({
      fecha: ahoraChile(),
      pagadoPorRol: undefined,
      pagadoPorUsuarioId: null,
      pagadoARol: "lavanderia_catalogo",
      pagadoAUsuarioId: lavSeleccionada?.id ?? null,
      rolDeAlojaFacil: "intermediario",
    });
    setModalOpen(true);
  };

  /**
   * Round 2026-05-12 iter 28: abre el modal en modo simplificado para
   * registrar un "Costo real" en la sub-tab homónimo de la Lavandería
   * General Externa. Pre-setea todo el shape del lavado:
   *  - rolDeAlojaFacil = "paga" (AF paga al proveedor externo)
   *  - pagadoPorRol = "administrador" (AF)
   *  - pagadoARol = "lavanderia_catalogo", pagadoAUsuarioId = id de la genérica
   *  - fecha = hoy
   * Ocultamos en el form todos los campos opcionales (cantidades, precios,
   * extras, propiedad, pagos) para que el admin solo vea fecha + nombre +
   * descripción + monto. El UI lo controla `LavadoForm.costoRealMode`.
   * Round 2026-05-12 iter 34: ahora costoRealMode mantiene visible la
   * sección de Pagos (antes simpleMode la ocultaba). El usuario puede
   * cambiar Rol AF / Pagado por si la realidad es distinta al default.
   */
  const handleNuevoCostoReal = () => {
    const generica = lavanderias.find((l) => l.es_generica);
    setEditingRecord(null);
    setCostoRealMode(true);
    form.resetFields();
    form.setFieldsValue({
      fecha: ahoraChile(),
      rolDeAlojaFacil: "paga",
      pagadoPorRol: "administrador",
      pagadoPorUsuarioId: null,
      pagadoARol: "lavanderia_catalogo",
      pagadoAUsuarioId: generica?.id ?? null,
    });
    setModalOpen(true);
  };

  /**
   * Round 2026-07-21: abre el modal para registrar un lavado 'intermediario'
   * (pass-through neto 0) en la Genérica Externa. Modelo: AF le cobra a
   * alguien (cliente/arrendatario) y le paga el MISMO monto al proveedor
   * externo. AF ni gana ni pierde → NO afecta la utilidad de AF Empresa (ni
   * suma ni resta). Reusa el modal simplificado (costoRealMode) porque
   * comparte el shape (fecha + nombre + descripción + monto + pagos). A
   * diferencia del costo real:
   *  - rolDeAlojaFacil = "intermediario".
   *  - pagadoPorRol SIN default: el admin elige a quién se le cobra (AF no
   *    puede ser el pagador en un intermediario — el form lo bloquea).
   *  - pagadoARol/UsuarioId = la genérica (locked), es el receptor.
   * Ver flujo-financiero.md §7.3 y modulo-lavanderia.md.
   */
  const handleNuevoIntermediado = () => {
    const generica = lavanderias.find((l) => l.es_generica);
    setEditingRecord(null);
    setCostoRealMode(true);
    form.resetFields();
    form.setFieldsValue({
      fecha: ahoraChile(),
      rolDeAlojaFacil: "intermediario",
      pagadoPorRol: undefined,
      pagadoPorUsuarioId: null,
      pagadoARol: "lavanderia_catalogo",
      pagadoAUsuarioId: generica?.id ?? null,
    });
    setModalOpen(true);
  };

  const handleEditar = (record: LavadoRecord) => {
    setEditingRecord(record);

    // Traducción tipo numérico → clave de rol del formulario. El tipo=5 de
    // un LAVADO siempre es una lavandería del catálogo (AF interna o
    // General Externa) identificada por `usuario_id` → `lavanderias.id`;
    // por eso mapea a "lavanderia_catalogo", que cubre a las dos (no solo a
    // las AF — ver la nota del tipo `Rol`).
    // El mismo tipo=5 en la tabla `gastos` significa otra cosa: receptor
    // externo con `recipient_id`, es decir otra empresa cualquiera y nunca
    // una lavandería. Acá leemos `lavados`, así que esa lectura no aplica
    // y este mapeo no debe copiarse tal cual a /flujos.
    const inferRol = (pago: any): Rol => {
      if (!pago) return "administrador";
      const tipoToRol: Record<number, Rol> = { 0: "administrador", 1: "propietario", 2: "arrendatario", 3: "aseador", 4: "trabajador", 5: "lavanderia_catalogo" };
      return tipoToRol[pago.tipo] ?? "trabajador";
    };

    // Round 2026-05-12 iter 38: para registros MANUALES (booking_id == null)
    // forzamos pagado_a a la lavandería del filtro. Esto cubre el caso de
    // un manual viejo que pudo crearse antes de iter 38 con otro destino
    // (ej. trabajador, propietario) — al re-editarlo lo normalizamos.
    // Para registros AUTO (booking_id != null) respetamos el pagado_a
    // que persistió la cascada (puede ser AF tipo=0 en la genérica, o
    // la AF interna tipo=5 con intermediario). La cascada lo regenera
    // si el admin pulsa Recalcular.
    const lavSeleccionada =
      typeof modo === "number" ? lavanderias.find((l) => l.id === modo) : null;
    const isManual = record.bookingId == null;
    const pagadoARolFinal = isManual
      ? ("lavanderia_catalogo" as Rol)
      : inferRol(record.pagadoA);
    const pagadoAUsuarioIdFinal = isManual
      ? lavSeleccionada?.id ?? null
      : typeof record.pagadoA === "object"
        ? record.pagadoA?.usuario_id ?? null
        : null;

    form.setFieldsValue({
      fecha: record.fecha ? dayjs(record.fecha) : null,
      propiedadId: record.propiedadId ?? null,
      cantidadLavados: record.cantidadLavados ?? 0,
      cantidadSecados: record.cantidadSecados ?? 0,
      precioLavado: record.precioLavado ?? 0,
      precioSecado: record.precioSecado ?? 0,
      monto: record.monto,
      descripcion: record.descripcion ?? "",
      nombre: record.nombre ?? "",
      rolDeAlojaFacil: record.rolDeAlojaFacil ?? "",
      extraDetalle: record.extraDetalle ?? "",
      extraMonto: record.extraMonto ?? 0,
      pagadoPorRol: inferRol(record.pagadoPor),
      pagadoPorUsuarioId: typeof record.pagadoPor === "object" ? record.pagadoPor?.usuario_id ?? null : null,
      pagadoARol: pagadoARolFinal,
      pagadoAUsuarioId: pagadoAUsuarioIdFinal,
    });
    // Round 2026-05-12 iter 34 (antes iter 28): reabrimos el modal en
    // `costoRealMode` cuando el lavado se ve como un costo real anotado
    // a mano (rol='paga' + nombre poblado + sin cantidades/extras). Eso
    // muestra el campo `nombre`, oculta detalle/extras y muestra Pagos
    // completos. Para lavados con cantidades reales (operativo AF interna
    // o estimados auto del booking) mostramos el formulario completo
    // tradicional.
    const looksCostoReal =
      record.rolDeAlojaFacil === "paga" &&
      (record.nombre != null && record.nombre !== "") &&
      !record.cantidadLavados &&
      !record.cantidadSecados &&
      !record.extraMonto;
    setCostoRealMode(looksCostoReal);
    setModalOpen(true);
  };

  const handleGuardar = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const body: Record<string, any> = {
        ...values,
        fecha: values.fecha ? values.fecha.format("YYYY-MM-DD") : null,
        monto: Number(values.monto ?? 0),
        extraMonto: Number(values.extraMonto ?? 0),
        cantidadLavados: Number(values.cantidadLavados ?? 0),
        cantidadSecados: Number(values.cantidadSecados ?? 0),
        precioLavado: Number(values.precioLavado ?? 0),
        precioSecado: Number(values.precioSecado ?? 0),
        // Round 2026-05-12 iter 28: nombre opcional (modal simple Costo Real).
        nombre: values.nombre ?? null,
      };

      const pagadoPor = buildPagoFromRol(values.pagadoPorRol, values.pagadoPorUsuarioId);
      const pagadoA = buildPagoFromRol(values.pagadoARol, values.pagadoAUsuarioId);

      // Validar que pagadoPor y pagadoA no sean la misma entidad — EXCEPTO
      // cuando rolDeAlojaFacil = "intermediario", donde el usuario pidió
      // explícitamente que toda combinación sea válida (round 2026-05-06).
      // En modo paga/recibe seguimos validando para no permitir circularidad
      // sin sentido financiero.
      const esIntermediario = values.rolDeAlojaFacil === "intermediario";
      if (!esIntermediario && pagadoPor && pagadoA && pagadoPor.tipo === pagadoA.tipo) {
        // Tipos sin usuario (AF, Propietario, Arrendatario): mismo tipo = circular
        if ([0, 1, 2].includes(pagadoPor.tipo)) {
          getMsg()?.error("'Pagado por' y 'Pagado a' no pueden ser la misma entidad");
          setSaving(false);
          return;
        }
        // Tipos con usuario (Aseador, Trabajador, Lavandería): circular si mismo usuario
        if (pagadoPor.usuario_id != null && pagadoPor.usuario_id === pagadoA.usuario_id) {
          getMsg()?.error("'Pagado por' y 'Pagado a' no pueden ser la misma persona");
          setSaving(false);
          return;
        }
      }

      body.pagadoPor = pagadoPor;
      body.pagadoA = pagadoA;
      delete body.pagadoPorRol;
      delete body.pagadoPorUsuarioId;
      delete body.pagadoARol;
      delete body.pagadoAUsuarioId;

      // Round 2026-05-11 iter 24: el body siempre lleva `lavanderia_id`
      // apuntando a una fila del catálogo. Si modo es un id numérico
      // (caso normal), va ese. Si modo === "general" (estado transitorio
      // antes del primer refreshLavanderias), defaulteamos al id de la
      // genérica para no crear un huérfano accidental — antes mandaba
      // NULL, lo que dejaba el lavado sin destino del catálogo.
      if (typeof modo === "number") {
        body.lavanderia_id = modo;
      } else {
        const generica = lavanderias.find((l) => l.es_generica);
        body.lavanderia_id = generica?.id ?? null;
      }
      let savedOk = false;
      if (editingRecord) {
        // Round 2026-05-12 iter 37: si el registro proviene de un
        // arriendo (bookingId != null), agregamos `confirmDesync: true`
        // al body. El componente lavanderia.tsx ya mostró el Modal.confirm
        // de advertencia antes de abrir el modal en modo editable, así
        // que llegar acá implica que el admin aceptó el riesgo.
        if (editingRecord.bookingId != null) {
          body.confirmDesync = true;
        }
        savedOk =
          (await finanzasEmpresaApi.updateLavado(editingRecord.id, body)) != null;
      } else {
        savedOk = (await finanzasEmpresaApi.createLavado(body)) != null;
      }

      setModalOpen(false);
      form.resetFields();
      setCostoRealMode(false);

      // Round 2026-07-21: si el registro guardado cae FUERA del período
      // filtrado (típico: la fecha por defecto es HOY pero el filtro apunta a
      // otro mes), navegamos el filtro al mes del registro para que aparezca.
      // Antes el usuario veía "creado con éxito" pero ningún registro nuevo en
      // la lista (el record existía, solo fuera de la vista). NO se toca la
      // fecha guardada: eso falsearía la atribución mensual del costo en el
      // cierre (ver flujo-financiero.md §7.2 y modulo-lavanderia.md).
      const fechaGuardada = body.fecha as string | null;
      const fueraDeRango =
        savedOk &&
        !!fechaGuardada &&
        effectiveRange != null &&
        (dayjs(fechaGuardada).isBefore(effectiveRange[0], "day") ||
          dayjs(fechaGuardada).isAfter(effectiveRange[1], "day"));
      if (fueraDeRango) {
        // Cambiar mes/mes dispara el refetch vía effectiveRange → useEffect,
        // así que no hace falta un fetchData(page) explícito acá.
        setDateMode("mes");
        setDateMonth(dayjs(fechaGuardada).startOf("month"));
        getMsg()?.info(
          `El registro se guardó con fecha ${dayjs(fechaGuardada).format(
            "DD-MM-YYYY",
          )}; ajusté el filtro a ese mes para mostrarlo.`,
        );
      } else {
        fetchData(page);
      }
    } catch (err: any) {
      if (err?.errorFields) return;
      getMsg()?.error(err.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleEliminar = async (id: number, opts?: { confirmDesync?: boolean }) => {
    try {
      // Round 2026-05-11 (iter 18): un solo endpoint delete. El dispatch
      // por `source` se eliminó (tabla unificada `lavados`).
      // Round 2026-05-12 iter 37: para registros con booking_id != null
      // el backend exige `confirmDesync=true` (rompe la coherencia con
      // limpieza_clp). El componente lavanderia.tsx muestra el aviso
      // con Modal.confirm antes de llamar acá con el flag.
      await finanzasEmpresaApi.deleteLavado(id, opts);
      fetchData(page);
    } catch (err: any) {
      getMsg()?.error(err.message || "Error al eliminar");
    }
  };

  // Round 2026-05-11 iter 24: el "mover" cambia el `lavanderia_id` del
  // registro a OTRA fila del catálogo, nunca a NULL. La distinción se
  // hace por `lavanderias.es_generica`:
  //   - Si el row vive en la genérica externa → mover a una AF interna
  //     (si hay > 1, abre modal para elegir destino).
  //   - Si vive en una AF interna o en huérfanos (legacy NULL) → mover
  //     a la genérica del catálogo.
  // Antes este handler operaba con `source` ("general"=NULL vs "af"=NOT NULL)
  // y dejaba lavados huérfanos al desasignar — eso contradice el modelo
  // actual donde toda lavado debería apuntar a alguna fila del catálogo.
  const handleMover = async (id: number, opts?: { confirmDesync?: boolean }) => {
    try {
      const generica = lavanderias.find((l) => l.es_generica);
      // El row vive en la lavandería del modo activo (la tabla solo
      // muestra rows del modo actual, así que el `lavanderia_id` real
      // del registro coincide con `modo` cuando es número).
      const lavSeleccionada =
        typeof modo === "number"
          ? lavanderias.find((l) => l.id === modo)
          : null;
      const viveEnGenerica = lavSeleccionada?.es_generica === true;

      if (viveEnGenerica) {
        // Mover a una AF interna (excluye la genérica del listado).
        const afInternas = lavanderias.filter(
          (l) => l.activo && !l.es_generica,
        );
        if (!afInternas.length) {
          getMsg()?.error("No hay lavanderías AF internas activas para mover el registro");
          return;
        }
        if (afInternas.length === 1) {
          await finanzasEmpresaApi.setLavadoLavanderia(id, afInternas[0].id, opts);
          fetchData(page);
        } else {
          setMoverRecordId(id);
          setMoverDestinoId(null);
          setMoverModalOpen(true);
          // Round 2026-05-12 iter 37: guardamos el flag para que
          // `handleConfirmarMover` lo propague al backend cuando el admin
          // termine de elegir destino.
          setMoverConfirmDesync(opts?.confirmDesync === true);
        }
      } else {
        // Vive en una AF interna o es huérfano legacy → mover a la genérica.
        if (!generica) {
          getMsg()?.error("No se encontró la lavandería genérica externa del sistema");
          return;
        }
        await finanzasEmpresaApi.setLavadoLavanderia(id, generica.id, opts);
        fetchData(page);
      }
    } catch (err: any) {
      getMsg()?.error(err.message || "Error al mover");
    }
  };

  const handleConfirmarMover = async () => {
    if (!moverRecordId || !moverDestinoId) {
      getMsg()?.warning("Selecciona una lavandería destino");
      return;
    }
    try {
      await finanzasEmpresaApi.setLavadoLavanderia(
        moverRecordId,
        moverDestinoId,
        moverConfirmDesync ? { confirmDesync: true } : undefined,
      );
      setMoverModalOpen(false);
      setMoverRecordId(null);
      setMoverDestinoId(null);
      setMoverConfirmDesync(false);
      fetchData(page);
    } catch (err: any) {
      getMsg()?.error(err.message || "Error al mover");
    }
  };

  const handleCancelarMover = () => {
    setMoverModalOpen(false);
    setMoverRecordId(null);
    setMoverDestinoId(null);
    setMoverConfirmDesync(false);
  };

  const handleCancelar = () => {
    setModalOpen(false);
    form.resetFields();
    setEditingRecord(null);
    setCostoRealMode(false);
  };

  const handleLimpiarFiltros = () => {
    // Reset al estado por defecto: mes actual.
    setDateMode("mes");
    setDateMonth(ahoraChile());
    setDateRange(null);
    setFiltroPropiedad([]);
    setMontoMin(null);
    setMontoMax(null);
    setExtraSearch("");
  };

  return {
    sidebarOpen,
    toggleSidebar,

    /**
     * true si la página se abrió con el drill-down de /finanzas-empresa.
     * La página lo usa para no restaurar las pestañas guardadas: el enlace
     * apunta a la tabla de registros y aterrizar en otra pestaña la esconde.
     */
    hayEnlaceDirecto,

    // Selector
    modo,
    lavanderias,
    handleModoChange,
    // Para el enlace directo: aplica la lavandería sin guardarla como preferida.
    handleModoChangeSinRecordar,
    refreshLavanderias,

    // Datos
    data,
    total,
    page,
    pageSize,
    setPageSize,
    loading,
    fetchData,
    // Round 2026-05-08 (iter 6): exponemos buildBody para que el botón
    // "Exportar a XLS" pueda iterar todas las páginas con los mismos
    // filtros que la vista actual (lavandería, fechas, propiedad, monto,
    // extras). Sin esto, el export quedaba desfasado respecto a la
    // tabla.
    buildBody,

    // Filtros
    // Round 2026-05-08: dateMode + dateMonth coexisten con dateRange.
    // El componente padre decide cuál picker mostrar según el toggle.
    dateMode,
    setDateMode,
    dateMonth,
    setDateMonth,
    dateRange,
    setDateRange,
    // Las mismas fechas, pero sin guardarlas: las usa el efecto del enlace
    // directo, donde el período lo trae la URL y no lo eligió el admin.
    setDateModeSinRecordar,
    setDateMonthSinRecordar,
    setDateRangeSinRecordar,
    effectiveRange,
    filtroPropiedad,
    setFiltroPropiedad,
    montoMin,
    setMontoMin,
    montoMax,
    setMontoMax,
    extraSearch,
    setExtraSearch,
    handleLimpiarFiltros,

    // Round 2026-05-12 iter 28: rolFilter + modo simple del modal +
    // agregados Estimado/Real para los Statistic cards de la genérica.
    rolFilter,
    setRolFilter,
    setRolFilterSinRecordar,
    costoRealMode,
    totalIngresoEstimado,
    totalCostoReal,
    totalIntermediado,

    // Modal / formulario
    modalOpen,
    editingRecord,
    saving,
    form,
    handleNuevo,
    handleNuevoCostoReal,
    handleNuevoIntermediado,
    handleEditar,
    handleGuardar,
    handleCancelar,

    // Acciones de tabla
    handleEliminar,
    handleMover,

    // Modal mover destino
    moverModalOpen,
    moverDestinoId,
    setMoverDestinoId,
    handleConfirmarMover,
    handleCancelarMover,

    // Datos auxiliares
    propiedades,
    usuarios,
    usuariosPorRol,
    crearOtro,
    ROLES,
  };
};
